/* Subtle image parallax for [data-cc-parallax] containers (image-banner,
   campaign-feature). Mirrors the theme's animate-on-scroll.js approach: a global
   behavioural script wired to a data attribute, not a per-section component.

   Model: [data-cc-parallax] is the STABLE clip container (measured for scroll
   progress, never transformed — so there's no measure/transform feedback). The
   inner LAYER it translates is, in order of preference:
     1. an explicit [data-cc-parallax-layer] descendant (used by image-banner,
        so the layer is the media wrapper and the intro zoom can keep scaling the
        inner <img> without the two transforms colliding), else
     2. the container's direct .img-fit child (campaign-feature), else
     3. the first element child.
   The layer is slightly oversized in parallax.css (8% each side) so it always
   covers the container as it travels ±8% of the container height.

   Robustness:
   - Skipped entirely under prefers-reduced-motion or without IntersectionObserver.
   - The scroll listener is IO-gated (only attached while in view) and
     rAF-throttled, so it's free off-screen.
   - transform: translate3d keeps it on the compositor; works on touch + desktop
     (purely scroll-position based — no background-attachment, which breaks iOS).
   - An initial apply() pre-positions each layer so nothing jumps on first view.
   - Idempotent (per-element marker), runs once globally, re-scans on
     shopify:section:load (theme editor). */
(function () {
  if (window.__ccParallaxInit) return;
  window.__ccParallaxInit = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  /* Fallback if the CSS variable can't be read (parallax.css always sets it). */
  var DEFAULT_STRENGTH = 0.18;

  function setup(container) {
    if (container.dataset.ccParallaxReady) return;
    var layer = container.querySelector('[data-cc-parallax-layer]')
      || container.querySelector(':scope > .img-fit')
      || container.firstElementChild;
    if (!layer) return;
    container.dataset.ccParallaxReady = '1';

    /* Read strength from the same CSS variable that sizes the oversize, so the
       travel and the headroom always match (no gap can ever be revealed). */
    var strength = parseFloat(getComputedStyle(container).getPropertyValue('--cc-parallax-strength')) || DEFAULT_STRENGTH;

    var ticking = false;
    var listening = false;

    function apply() {
      ticking = false;
      var h = container.offsetHeight;
      if (!h) return;
      var rect = container.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // -1 when the container sits fully below the viewport centre (entering),
      // +1 when fully above (leaving); 0 when centred.
      var center = rect.top + rect.height / 2;
      var progress = (center - vh / 2) / (vh / 2 + rect.height / 2);
      if (progress > 1) progress = 1; else if (progress < -1) progress = -1;
      var offset = -progress * h * strength;
      layer.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }

    var io = new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      if (visible && !listening) {
        listening = true;
        window.addEventListener('scroll', onScroll, { passive: true });
        apply();
      } else if (!visible && listening) {
        listening = false;
        window.removeEventListener('scroll', onScroll);
      }
    });
    io.observe(container);
    window.addEventListener('on:debounced-resize', apply);

    // Pre-position so the layer is already at the right offset before it
    // scrolls into view (no jump on first reveal).
    apply();
  }

  function scan() {
    document.querySelectorAll('[data-cc-parallax]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  // Theme editor: a re-rendered section brings new [data-cc-parallax] nodes.
  document.addEventListener('shopify:section:load', scan);
})();
